/**
 * Calculate age from birth date
 * Returns a human-readable age string like "2 weeks old", "3 months old", "1 year 2 months old"
 */
export function calculateAge(birthDateString: string | null | undefined): string {
  if (!birthDateString) {
    return 'Age unknown';
  }

  const birthDate = new Date(birthDateString);
  const now = new Date();

  // Validate date
  if (isNaN(birthDate.getTime())) {
    return 'Invalid date';
  }

  // Calculate difference
  const diffMs = now.getTime() - birthDate.getTime();

  // If birth date is in the future
  if (diffMs < 0) {
    return 'Not born yet';
  }

  // Calculate time units
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Less than 1 day
  if (diffDays === 0) {
    return 'Born today';
  }

  // Less than 2 weeks - show days
  if (diffDays < 14) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} old`;
  }

  // Less than 8 weeks - show weeks
  if (diffDays < 56) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'} old`;
  }

  // Calculate months and years
  let years = now.getFullYear() - birthDate.getFullYear();
  let months = now.getMonth() - birthDate.getMonth();

  // Adjust for negative months
  if (months < 0) {
    years--;
    months += 12;
  }

  // Adjust if the day hasn't occurred yet this month
  if (now.getDate() < birthDate.getDate()) {
    months--;
    if (months < 0) {
      years--;
      months += 11;
    }
  }

  // Format output
  if (years === 0) {
    return `${months} month${months === 1 ? '' : 's'} old`;
  } else if (months === 0) {
    return `${years} year${years === 1 ? '' : 's'} old`;
  } else {
    return `${years} year${years === 1 ? '' : 's'} ${months} month${months === 1 ? '' : 's'} old`;
  }
}

/**
 * Format birth date for display
 */
export function formatBirthDate(birthDateString: string | null | undefined): string {
  if (!birthDateString) {
    return 'Unknown';
  }

  const birthDate = new Date(birthDateString);

  // Validate date
  if (isNaN(birthDate.getTime())) {
    return 'Invalid date';
  }

  // Format as "Jan 15, 2024"
  return birthDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}
